// ============================================================
// ByOurConsent.com -- Chapter Application Page
// /chapters/apply
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api    from '../utils/api';
import toast  from 'react-hot-toast';

const iStyle = {
    width: '100%', background: '#f5f0e8',
    border: '0.5px solid #c8beaa', padding: '9px 12px',
    fontSize: '14px', fontFamily: 'sans-serif',
    color: '#1e2a3a', borderRadius: '3px',
    outline: 'none', boxSizing: 'border-box'
};
const lStyle = {
    display: 'block', fontSize: '11px',
    fontFamily: 'sans-serif', color: '#6a7080',
    letterSpacing: '0.08em', textTransform: 'uppercase',
    marginBottom: '5px'
};
const Field = ({ label, required, children }) => (
    <div style={{ marginBottom: '1rem' }}>
        <label style={lStyle}>{label}{required && <span style={{ color: '#8b1a1a' }}> *</span>}</label>
        {children}
    </div>
);

const ChapterApplyPage = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        applicant_name:  '',
        email:           '',
        phone:           '',
        chapter_name:    '',
        chapter_type:    'concept',
        description:     '',
        purpose:         '',
        location:        '',
        social_facebook: '',
        social_twitter:  '',
        social_instagram:'',
        social_website:  '',
        meeting_type:    'none',
        meeting_info:    '',
        why_lead:        ''
    });

    // Require login
    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate('/login', { state: { from: { pathname: '/chapters/apply' } } });
            return;
        }
        // Pre-fill from account
        setForm(p => ({
            ...p,
            applicant_name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
            email:          user.email || ''
        }));
    }, [user, loading, navigate]);

    if (loading) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(p => ({ ...p, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!form.applicant_name || !form.email || !form.chapter_name) {
            toast.error('Please fill in all required fields');
            return;
        }
        setSubmitting(true);
        try {
            await api.post('/chapters/apply', form);
            setSubmitted(true);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Submission failed');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) return (
        <>
            <Nav />
            <div style={{ maxWidth: '560px', margin: '4rem auto', padding: '0 1.5rem', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '1rem' }}>🏛️</div>
                <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '10px' }}>
                    Application submitted!
                </h1>
                <p style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.7, marginBottom: '2rem' }}>
                    Thank you for applying to start a chapter. Our team will review your application and be in touch within a few days.
                </p>
                <Link to="/chapters" style={{ background: '#8b1a1a', color: '#f5f0e8', padding: '10px 24px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px' }}>
                    Back to chapters
                </Link>
            </div>
            <Footer />
        </>
    );

    return (
        <>
            <Helmet>
                <title>Start a Chapter — By Our Consent</title>
            </Helmet>

            <Nav />

            {/* Header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '660px', margin: '0 auto' }}>
                    <Link to="/chapters" style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Chapters
                    </Link>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '8px', marginBottom: '4px' }}>
                        Start a chapter
                    </h1>
                    <p style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa' }}>
                        Apply to lead a location-based or concept-based chapter. We'll review and get back to you within a few days.
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: '660px', margin: '0 auto', padding: '2rem 1.5rem' }}>

                {/* Your info */}
                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '17px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem', paddingBottom: '8px', borderBottom: '0.5px solid #c8beaa' }}>
                        Your information
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <Field label="Full name" required>
                            <input style={{ ...iStyle, background:'#ede8dc', color:'#6a7080' }} name="applicant_name" value={form.applicant_name} readOnly />
                        </Field>
                        <Field label="Phone (optional)">
                            <input style={iStyle} name="phone" value={form.phone} onChange={handleChange} placeholder="555-555-5555" />
                        </Field>
                    </div>
                    <Field label="Email address" required>
                        <input style={{ ...iStyle, background:'#ede8dc', color:'#6a7080' }} type="email" name="email" value={form.email} readOnly />
                    </Field>
                    <div style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070', marginTop:'-8px' }}>
                        Name and email pulled from your account. <Link to="/account" style={{ color:'#8b1a1a' }}>Update in account settings</Link> if needed.
                    </div>
                </div>

                {/* Chapter info */}
                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '17px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem', paddingBottom: '8px', borderBottom: '0.5px solid #c8beaa' }}>
                        Chapter details
                    </div>

                    <Field label="Chapter name" required>
                        <input style={iStyle} name="chapter_name" value={form.chapter_name} onChange={handleChange} placeholder="By Our Consent — Austin, TX  or  By Our Consent — Anti-Corruption" />
                    </Field>

                    <Field label="Chapter type" required>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            {[
                                { value: 'location', label: '📍 Location based', sub: 'City, state, or region' },
                                { value: 'concept',  label: '💡 Concept based',  sub: 'Issue, cause, or topic' }
                            ].map(opt => (
                                <div key={opt.value} onClick={() => setForm(p => ({...p, chapter_type: opt.value}))}
                                    style={{ flex: 1, padding: '12px', border: `0.5px solid ${form.chapter_type === opt.value ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer', background: form.chapter_type === opt.value ? '#fff5f5' : '#f5f0e8' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e2a3a', fontFamily: 'sans-serif' }}>{opt.label}</div>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '2px' }}>{opt.sub}</div>
                                </div>
                            ))}
                        </div>
                    </Field>

                    {form.chapter_type === 'location' && (
                        <Field label="Location">
                            <input style={iStyle} name="location" value={form.location} onChange={handleChange} placeholder="Austin, TX  or  Pacific Northwest" />
                        </Field>
                    )}

                    <Field label="Description">
                        <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3} name="description" value={form.description} onChange={handleChange}
                            placeholder="Brief description of this chapter (shown publicly)" />
                    </Field>

                    <Field label="Purpose / goals">
                        <textarea style={{ ...iStyle, resize: 'vertical' }} rows={4} name="purpose" value={form.purpose} onChange={handleChange}
                            placeholder="What does this chapter aim to accomplish? What civic issues will it focus on?" />
                    </Field>
                </div>

                {/* Meeting info */}
                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '17px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem', paddingBottom: '8px', borderBottom: '0.5px solid #c8beaa' }}>
                        Meetings (optional)
                    </div>
                    <Field label="Meeting format">
                        <select style={iStyle} name="meeting_type" value={form.meeting_type} onChange={handleChange}>
                            <option value="none">No regular meetings planned</option>
                            <option value="zoom">Online (Zoom/video)</option>
                            <option value="in_person">In person</option>
                            <option value="hybrid">Hybrid (online + in person)</option>
                        </select>
                    </Field>
                    {form.meeting_type !== 'none' && (
                        <Field label="Meeting details">
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3} name="meeting_info" value={form.meeting_info} onChange={handleChange}
                                placeholder="When, how often, where (or Zoom link) — e.g. Every Tuesday 7pm EST via Zoom" />
                        </Field>
                    )}
                </div>

                {/* Social links */}
                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '17px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem', paddingBottom: '8px', borderBottom: '0.5px solid #c8beaa' }}>
                        Social links (optional)
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <Field label="Facebook"><input style={iStyle} name="social_facebook" value={form.social_facebook} onChange={handleChange} placeholder="facebook.com/..." /></Field>
                        <Field label="Twitter / X"><input style={iStyle} name="social_twitter" value={form.social_twitter} onChange={handleChange} placeholder="@handle" /></Field>
                        <Field label="Instagram"><input style={iStyle} name="social_instagram" value={form.social_instagram} onChange={handleChange} placeholder="@handle" /></Field>
                        <Field label="Website"><input style={iStyle} name="social_website" value={form.social_website} onChange={handleChange} placeholder="https://..." /></Field>
                    </div>
                </div>

                {/* Why lead */}
                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '17px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem', paddingBottom: '8px', borderBottom: '0.5px solid #c8beaa' }}>
                        Tell us about yourself
                    </div>
                    <Field label="Why do you want to lead this chapter?">
                        <textarea style={{ ...iStyle, resize: 'vertical' }} rows={5} name="why_lead" value={form.why_lead} onChange={handleChange}
                            placeholder="Tell us about your background, motivation, and what you hope to accomplish as a chapter leader..." />
                    </Field>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
                    <Link to="/chapters" style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '10px 20px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', textDecoration: 'none', borderRadius: '3px' }}>
                        Cancel
                    </Link>
                    <button onClick={handleSubmit} disabled={submitting}
                        style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 28px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', cursor: submitting ? 'default' : 'pointer', borderRadius: '3px', opacity: submitting ? 0.7 : 1 }}>
                        {submitting ? 'Submitting...' : 'Submit application →'}
                    </button>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default ChapterApplyPage;
